import dayjs from 'dayjs';
import { submitClaim } from 'feature/claims/claimSlice';
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import {
	Button,
	DatePicker,
	FormControl,
	Stack,
	TextField,
	Typography,
} from 'styles';
import { IClaim } from 'types/Claim';
import { PageProps } from 'types/newClaimPageType';
import { INVALID_DATE, INVOICE_NUMBER_MAX_LENGTH } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import {
	formatPrice,
	formatZuluForBE,
	getNumberFromString,
	handleValidationLettersNumbers,
} from 'utils/utils';

const Page3: FC<PageProps> = props => {
	const { isLastPage, formData, setFormData, submitPage } = props;
	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});

	const handleChange = (name: string, value: string | null) => {
		const initialReceipt = formData.receipt ? formData.receipt : {};
		const newReceipt = { ...initialReceipt, [name]: value };
		setFormData((prev: IClaim) => ({ ...prev, receipt: newReceipt }));
	};

	const [totalAmount, setTotalAmount] = useState<string>(
		formData?.receipt?.totalInvoiceAmount
			? formatPrice(formData?.receipt?.totalInvoiceAmount)
			: '',
	);

	return (
		<Stack gap="52px">
			<Stack gap="40px">
				<Typography variant="title6">
					{tClientLabels('newClaim.selectedPage.sectionTitile.invoice')}
				</Typography>
				<Stack direction="row" gap="27px">
					<FormControl sx={{ width: '310px' }}>
						<TextField
							name="inputInvoiceNumber"
							type="text"
							label={tClientLabels('check-and-submit.searchLabels.invoiceNumber')}
							onChange={e => {
								//invoice number length is limited
								if (e.target.value.length <= INVOICE_NUMBER_MAX_LENGTH)
									handleChange('number', e.target.value);
							}}
							autoComplete={'off'}
							value={formData?.receipt?.number || ''}
						/>
					</FormControl>
					<FormControl sx={{ width: '310px' }}>
						<DatePicker
							variant="margin"
							label={tClientLabels('newClaim.searchLabels.invoiceDate')}
							value={
								formData?.receipt?.receiptDate
									? dayjs(formData?.receipt?.receiptDate).utcOffset(0)
									: null
							}
							onChange={value => handleChange('receiptDate', formatZuluForBE(value))}
							maxDate={dayjs().utcOffset(0)}
						/>
					</FormControl>
					<FormControl sx={{ width: '310px' }}>
						<TextField
							name="inputBillingEntityName"
							type="text"
							label={tClientLabels('check-and-submit.searchLabels.billingEntityName')}
							onChange={e => {
								if (handleValidationLettersNumbers(e.target.value))
									handleChange('name', e.target.value);
							}}
							autoComplete={'off'}
							value={formData?.receipt?.name || ''}
						/>
					</FormControl>
					<FormControl sx={{ width: '310px' }}>
						<TextField
							name="inputVATNumber"
							type="text"
							label={tClientLabels('check-and-submit.searchLabels.VATNumber')}
							onChange={e => {
								if (handleValidationLettersNumbers(e.target.value))
									handleChange('vatCode', e.target.value);
							}}
							autoComplete={'off'}
							value={formData?.receipt?.vatCode || ''}
						/>
					</FormControl>
					<FormControl sx={{ width: '310px' }}>
						<TextField
							name="inputTotalInvoiceAmount"
							type="text"
							label={tClientLabels('check-and-submit.searchLabels.totalInvoiceAmount')}
							onChange={e => {
								handleChange('totalInvoiceAmount', getNumberFromString(e.target.value));
								setTotalAmount(e.target.value);
							}}
							onBlur={e => {
								setTotalAmount(formatPrice(getNumberFromString(e.target.value)));
								handleChange(
									'totalInvoiceAmount',
									getNumberFromString(formatPrice(getNumberFromString(e.target.value))),
								);
							}}
							autoComplete={'off'}
							value={totalAmount}
						/>
					</FormControl>
				</Stack>
				<Button
					variant="contained"
					disabled={
						!(
							formData?.receipt?.number &&
							formData?.receipt?.receiptDate &&
							formData?.receipt?.receiptDate !== INVALID_DATE &&
							formData?.receipt?.name &&
							formData?.receipt?.vatCode &&
							formData?.receipt?.totalInvoiceAmount
						)
					}
					sx={{ width: 'fit-content' }}
					onClick={() => {
						isLastPage ? dispatch(submitClaim(formData)) : submitPage!();
					}}
				>
					{tClientLabels(
						isLastPage ? 'newClaim.submitClaim' : 'newClaim.selectedPage.proceed',
					)}
				</Button>
			</Stack>
		</Stack>
	);
};

export default Page3;
